<template>
    <div class="signup">
        <h1>Create your MyStekz account</h1>
        <div style="background: white">
            <Message v-if="errorMessage" :closable="false" severity="error">
                {{ errorMessage }}
            </Message>
        </div>

        <div v-if="showEmailPasswordForm" class="signup-form">
            <form @submit.prevent="handleSignUp">
                <div class="input email">
                    <label for="email">Email</label>
                    <InputText
                        id="email"
                        v-model="email"
                        type="email"
                        autocomplete="email"
                        :class="{ 'p-invalid': submitted && !email }"
                        aria-describedby="email-error"
                    />
                    <small
                        id="email-error"
                        class="p-error"
                        v-if="submitted && !email"
                    >
                        Email is required
                    </small>
                </div>
                <div class="input password">
                    <label for="password">Password</label>
                    <Password
                        id="password"
                        v-model="password"
                        :inputProps="{
                            autocomplete: 'new-password',
                        }"
                        :class="{ 'p-invalid': submitted && !password }"
                        :feedback="true"
                        aria-describedby="password-error"
                        toggleMask
                    />
                    <small
                        id="password-error"
                        class="p-error"
                        v-if="submitted && !password"
                    >
                        Password is required
                    </small>
                </div>
                <div class="input password">
                    <label for="confirmPassword">Confirm Password</label>
                    <Password
                        id="confirmPassword"
                        v-model="confirmPassword"
                        :inputProps="{
                            autocomplete: 'new-password',
                        }"
                        :class="{
                            'p-invalid':
                                (submitted && !confirmPassword) ||
                                (submitted && password !== confirmPassword),
                        }"
                        :feedback="false"
                        aria-describedby="confirm-password-error"
                        toggleMask
                    />
                    <small
                        id="confirm-password-error"
                        class="p-error"
                        v-if="submitted && !confirmPassword"
                    >
                        Please confirm your password
                    </small>
                    <small
                        id="password-match-error"
                        class="p-error"
                        v-if="submitted && password !== confirmPassword"
                    >
                        Passwords do not match
                    </small>
                </div>
                <div class="input name">
                    <label for="name">Full Name</label>
                    <InputText
                        id="name"
                        v-model="name"
                        type="text"
                        autocomplete="name"
                        :class="{ 'p-invalid': submitted && !name }"
                        aria-describedby="name-error"
                    />
                    <small
                        id="name-error"
                        class="p-error"
                        v-if="submitted && !name"
                    >
                        Name is required
                    </small>
                </div>

                <div class="buttons">
                    <Button
                        type="submit"
                        class="p-button-raised button"
                        label="Create Account"
                        :loading="loading"
                    />
                    <div class="login-link">
                        Already have an account?
                        <router-link to="/login">Sign in</router-link>
                    </div>
                </div>
            </form>
        </div>

        <div v-else class="signup-options">
            <p>Choose how to create your account</p>
            <div class="buttons">
                <Button
                    class="p-button-raised button"
                    label="Sign up with Google"
                    @click="handleGoogleSignUp"
                    :loading="loading"
                >
                    <template #icon>
                        <img
                            class="google-icon"
                            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                            alt="Google icon"
                        />
                    </template>
                </Button>
                <Button
                    class="p-button-raised button"
                    label="Sign up with Email"
                    icon="pi pi-envelope"
                    @click="showEmailPasswordForm = true"
                >
                    <template #icon>
                        <img
                            class="mail-icon"
                            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/mail.svg"
                            alt="Email icon"
                        />
                    </template>
                </Button>
                <div class="login-link">
                    Already have an account?
                    <router-link to="/login">Sign in</router-link>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useAuthenticationStore } from "@/stores/authentication-store";
import { useRouter } from "vue-router";

const { signupUserWithGoogle, signupUserWithEmailAndPassword } =
    useAuthenticationStore();
const router = useRouter();

const email = ref("");
const password = ref("");
const confirmPassword = ref("");
const name = ref("");
const errorMessage = ref("");
const loading = ref(false);
const submitted = ref(false);
const showEmailPasswordForm = ref(false);

const handleSignUp = async () => {
    submitted.value = true;

    if (
        !email.value ||
        !password.value ||
        !confirmPassword.value ||
        !name.value ||
        password.value !== confirmPassword.value
    ) {
        return;
    }

    loading.value = true;
    errorMessage.value = "";

    try {
        await signupUserWithEmailAndPassword(
            email.value,
            password.value,
            name.value
        );
        // The auth store will automatically handle email verification and MFA setup
        // through dialogs based on the auth status
        router.push("/");
    } finally {
        loading.value = false;
    }
};

const handleGoogleSignUp = async () => {
    loading.value = true;
    errorMessage.value = "";

    try {
        await signupUserWithGoogle();
        router.push("/");
    } finally {
        loading.value = false;
    }
};
</script>

<style lang="scss" scoped>
.signup {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    height: 100vh;
    padding: 30px;
    align-items: center;
    color: var(--stekz-white);
    background-color: var(--stekz-primary-color);

    h1 {
        color: var(--stekz-white);
        margin-bottom: 2rem;
    }

    .signup-form,
    .signup-options {
        max-width: 320px;
        width: 100%;
    }

    .signup-options {
        text-align: center;

        p {
            margin-bottom: 1.5rem;
            font-size: 1.1rem;
        }
    }
}

.input {
    margin-bottom: 1.5rem;

    &.email,
    &.password,
    &.name {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    label {
        font-weight: 500;
    }

    .p-password {
        width: 100%;
    }
}

.buttons {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin-top: 2rem;
}

.button {
    background-color: var(--stekz-dev-color);
    color: var(--stekz-white);
    width: 100%;

    &.button-back {
        background-color: transparent;
        border-color: var(--stekz-white);
        color: var(--stekz-white);
    }
}

.login-link {
    text-align: center;
    margin-top: 0.5rem;

    a {
        color: var(--stekz-white);
        text-decoration: underline;

        &:hover {
            text-decoration: none;
        }
    }
}

.google-icon,
.mail-icon {
    border: none;
    display: inline-block;
    height: 18px;
    vertical-align: middle;
    width: 18px;
    margin-right: 8px;
}

:deep(.p-password-input) {
    width: 100%;
}
</style>
